import React, { useEffect, useState } from 'react';
import { 
  ArrowRight, 
  CheckCircle2, 
  KeyRound, 
  Network, 
  ShieldCheck, 
  UserCircle, 
  Waypoints,
  Users,
  UserPlus,
  Building2,
  Sliders,
  Check,
  XCircle,
  AlertTriangle,
  Lock,
  ChevronDown,
  ChevronUp,
  Plus
} from 'lucide-react';
import { AuditLogEntry, AuthSession, OrganizationMember, TenantWorkspace } from '../types';
import { api } from '../services/api';

interface AccountProps {
  session: AuthSession;
  setActiveTab: (tab: string) => void;
  onSwitchRole?: (role: 'owner' | 'admin' | 'operator' | 'viewer') => void;
  tenants?: TenantWorkspace[];
  activeTenant?: TenantWorkspace | null;
  onSwitchTenant?: (tenant: TenantWorkspace) => void;
}

const roleCopy: Record<NonNullable<AuthSession['organization']>['role'], { label: string; description: string; access: string[] }> = {
  owner: {
    label: 'Workspace Owner',
    description: 'Full control of organization settings, applications, policies, retention, and governance decisions.',
    access: ['Manage tenant settings & member roles', 'Author & delete custom policies', 'Manage applications & integration keys', 'Configure retention controls', 'Export NIST AI RMF / SOC 2-mapped evidence bundles']
  },
  admin: {
    label: 'AI Administrator',
    description: 'Oversee all registered AI applications, fleet safety posture, and global security policies.',
    access: ['Manage applications and integration keys', 'Author & delete custom policies', 'Configure R2 cold storage retention', '1-Click quarantine compromised agents', 'Review audit logs & evidence']
  },
  operator: {
    label: 'AI Developer / Operator',
    description: 'Connect AI applications, maintain agent safety profiles, run behavioral validation, and respond to incidents.',
    access: ['Register applications & rotate scoped keys', 'Create & edit agent safety profiles', 'Run pre-flight validation suites', 'Triage incidents & test simulator actions']
  },
  viewer: {
    label: 'Read-Only Auditor / Viewer',
    description: 'Explore organization posture, policies, traces, and evidence without changing workspace state.',
    access: ['View organization posture & incidents', 'Inspect multi-agent delegation DAG', 'Read profiles, policies, & traces', 'Download compliance verification reports']
  }
};

export const Account: React.FC<AccountProps> = ({ 
  session, 
  setActiveTab, 
  onSwitchRole,
  tenants = [],
  activeTenant,
  onSwitchTenant
}) => {
  const organization = session.organization;
  const user = session.user;
  const role = organization?.role || 'admin';
  const details = roleCopy[role] || roleCopy.admin;
  const canManageApplications = role !== 'viewer';
  const canAdminister = Boolean(!session.demo_mode && (role === 'owner' || role === 'admin'));
  
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState<'owner' | 'admin' | 'operator' | 'viewer'>('operator');
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [memberError, setMemberError] = useState<string | null>(null);
  const [membersLoading, setMembersLoading] = useState(true);

  // Tenant management state
  const [showCreateTenantModal, setShowCreateTenantModal] = useState(false);
  const [newTenantName, setNewTenantName] = useState('');
  const [newTenantSlug, setNewTenantSlug] = useState('');
  const [newTenantEnv, setNewTenantEnv] = useState<'production' | 'staging' | 'sandbox'>('production');
  const [newTenantFramework, setNewTenantFramework] = useState('SOC 2 Type II');
  const [tenantSuccess, setTenantSuccess] = useState<string | null>(null);
  const [tenantError, setTenantError] = useState<string | null>(null);
  const [isTenantsCollapsed, setIsTenantsCollapsed] = useState(false);

  const [tenantsList, setTenantsList] = useState<TenantWorkspace[]>(tenants);
  const currentTenant = activeTenant || tenantsList.find(t => t.id === organization?.id) || tenantsList[0];

  useEffect(() => {
    setTenantsList(tenants);
  }, [tenants]);

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTenantName.trim()) return;
    const slug = newTenantSlug.trim() || newTenantName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    try {
      const res = await api.createTenant({
        name: newTenantName.trim(),
        slug,
        environment: newTenantEnv,
        compliance_framework: newTenantFramework
      });
      const createdTenant = res;
      setTenantsList(prev => [...prev, createdTenant]);
      setTenantSuccess(`Sub-tenant workspace "${createdTenant.name}" provisioned.`);
      if (onSwitchTenant) onSwitchTenant(createdTenant);
    } catch (error) {
      setTenantError(error instanceof Error ? error.message : 'Unable to create this workspace.');
      return;
    }
    setNewTenantName('');
    setNewTenantSlug('');
    setShowCreateTenantModal(false);
    setTimeout(() => setTenantSuccess(null), 4000);
  };

  const [teamMembers, setTeamMembers] = useState<OrganizationMember[]>([]);

  useEffect(() => {
    Promise.all([api.getAuditLog(25), api.getMembers()])
      .then(([audit, members]) => {
        setAuditLog(audit);
        setTeamMembers(members);
      })
      .catch((error) => {
        setAuditLog([]);
        setTeamMembers([]);
        setMemberError(error instanceof Error ? error.message : 'Member directory is unavailable.');
      })
      .finally(() => {
        setAuditLoading(false);
        setMembersLoading(false);
      });
  }, [organization?.id]);

  const handleRoleChange = async (memberId: string, targetRole: 'owner' | 'admin' | 'operator' | 'viewer') => {
    setMemberError(null);
    try {
      await api.updateMemberRole(memberId, targetRole);
      setTeamMembers(prev => prev.map(m => m.id === memberId ? { ...m, role: targetRole } : m));
    } catch (error) {
      setMemberError(error instanceof Error ? error.message : 'Unable to update this member role.');
    }
  };

  const handleRemoveMember = async (member: OrganizationMember) => {
    if (!window.confirm(member.status === 'invited' ? `Revoke the invitation for ${member.email}?` : `Remove ${member.email} from this workspace?`)) return;
    setMemberError(null);
    try {
      if (member.status === 'invited') await api.revokeInvitation(member.id);
      else await api.removeMember(member.id);
      setTeamMembers(prev => prev.filter(m => m.id !== member.id));
    } catch (error) {
      setMemberError(error instanceof Error ? error.message : 'Unable to update workspace membership.');
    }
  };

  const handleInviteMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setMemberError(null);
    try {
      const invitation = await api.inviteMember({ email: newEmail.trim(), name: newName.trim() || undefined, role: newRole });
      setTeamMembers(prev => [...prev, invitation]);
      setInviteSuccess(`Invitation recorded for ${invitation.email}. They will join after signing in through the organization identity service.`);
      setNewEmail('');
      setNewName('');
      setShowInviteModal(false);
      setTimeout(() => setInviteSuccess(null), 5000);
    } catch (error) {
      setMemberError(error instanceof Error ? error.message : 'Unable to create the invitation.');
    }
  };

  const auditLabel = (entry: AuditLogEntry) => entry.action.replace(/[._]/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

  return (
    <div className="mx-auto max-w-6xl space-y-8 animate-fade-in">
      
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-300">
          <UserCircle className="h-4 w-4" />
          <span>Tenant Organization & RBAC Workspace</span>
        </div>
        <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-3xl">
          Workspace & Access Control
        </h1>
        <p className="mt-1.5 max-w-3xl text-sm text-slate-600 dark:text-slate-400">
          Manage your organization tenant, team member role-based access control (RBAC), and active persona view.
        </p>
      </div>

      {inviteSuccess && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/20 dark:text-emerald-300 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          <span>{inviteSuccess}</span>
        </div>
      )}

      {/* Profile & Active Persona Card */}
      <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
        <section className="glass-panel rounded-2xl p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-xl font-black text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
              {(user?.name || user?.email || 'A').slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-xl font-bold text-slate-900 dark:text-white">{user?.name || 'Enterprise Operator'}</h2>
              <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">{user?.email || 'operator@agenticscale.org'}</p>
              <div className="mt-3 flex items-center gap-2">
                <span className="inline-flex rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-brand-700 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300">
                  {details.label}
                </span>
                {session.demo_mode && (
                  <span className="inline-flex rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                    Live Demo Mode
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/50">
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Organization Tenant</div>
              <div className="mt-1 text-sm font-bold text-slate-900 dark:text-white">{organization?.name || 'org-demo-agenticscale'}</div>
              <div className="mt-0.5 text-[10px] font-mono text-slate-400">ID: {organization?.id || 'org-demo'}</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/50">
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Active Persona View</div>
              <div className="mt-1 text-sm font-bold capitalize text-slate-900 dark:text-white">{role}</div>
              <div className="mt-0.5 text-[10px] text-brand-600 dark:text-brand-400">Use top navbar pill to switch</div>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-brand-200 bg-brand-50 p-6 dark:border-brand-500/20 dark:bg-brand-500/10 sm:p-8">
          <div className="flex items-center gap-2 text-brand-700 dark:text-brand-300">
            <ShieldCheck className="h-5 w-5" />
            <h2 className="text-lg font-bold">Permissions for {details.label}</h2>
          </div>
          <p className="mt-3 text-sm leading-6 text-brand-950 dark:text-brand-100">{details.description}</p>
          <div className="mt-5 space-y-2 text-xs text-brand-900 dark:text-brand-100">
            {details.access.map((item) => (
              <div key={item} className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {tenantSuccess && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/20 dark:text-emerald-300 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          <span>{tenantSuccess}</span>
        </div>
      )}
      {tenantError && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/20 dark:text-rose-300 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
          <span>{tenantError}</span>
        </div>
      )}

      {/* Enterprise Multi-Tenant Workspaces Section */}
      <section className="glass-panel rounded-2xl p-6 sm:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <span>Enterprise Tenants & Workspaces</span>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold border border-slate-200 dark:border-slate-700">
                  {tenantsList.length} Tenants
                </span>
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">Strictly partition fleet agents, policies, incidents, and audit logs by sub-organization tenant</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {canAdminister && (
              <button
                type="button"
                onClick={() => setShowCreateTenantModal(true)}
                className="inline-flex items-center gap-1.5 rounded-xl bg-brand-600 px-3.5 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-brand-500"
              >
                <Plus className="h-4 w-4" />
                <span>Provision Sub-Tenant</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsTenantsCollapsed(!isTenantsCollapsed)}
              className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
              aria-expanded={!isTenantsCollapsed}
              title={isTenantsCollapsed ? 'Expand Tenants' : 'Collapse Tenants'}
            >
              {isTenantsCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Tenant Cards Grid */}
        {!isTenantsCollapsed && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-in">
            {tenantsList.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 p-5 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                No organization workspaces are assigned to this account yet.
              </div>
            ) : tenantsList.map((t) => {
              const isCurrent = t.id === currentTenant?.id;
              return (
                <div 
                  key={t.id} 
                  className={`p-5 rounded-2xl border transition-all ${
                    isCurrent 
                      ? 'border-brand-300 dark:border-brand-500/50 bg-brand-50/40 dark:bg-brand-950/20 shadow-sm' 
                      : 'border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/60'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm text-slate-900 dark:text-white">{t.name}</span>
                        <span className={`text-[9px] px-2 py-0.5 rounded font-mono font-bold uppercase ${
                          t.environment === 'production' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400'
                        }`}>
                          {t.environment}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 font-mono mt-1">
                        ID: <code className="font-bold">{t.id}</code> · Slug: {t.slug}
                      </div>
                    </div>

                    {isCurrent ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-brand-700 dark:text-brand-300 bg-brand-100 dark:bg-brand-500/20 px-2 py-0.5 rounded-full">
                        <Check className="w-3 h-3" />
                        <span>Active</span>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onSwitchTenant && onSwitchTenant(t)}
                        className="inline-flex items-center gap-1 text-xs font-bold text-brand-600 hover:text-brand-500 dark:text-brand-400 p-1.5 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-brand-300 shadow-2xs transition"
                      >
                        <span>Switch to this</span>
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    )}
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs text-slate-600 dark:text-slate-400">
                    <span>Compliance: <strong className="text-slate-800 dark:text-slate-200">{t.compliance_framework}</strong></span>
                    <span>{t.agent_count} Active Agents · {t.open_incidents} Incidents</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Modal: Provision Sub-Tenant */}
      {showCreateTenantModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-xs p-4 animate-fade-in">
          <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-brand-600 dark:text-brand-400" />
                <h3 className="text-base font-bold text-slate-900 dark:text-white">Provision Sub-Tenant Workspace</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateTenantModal(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-white"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateTenant} className="p-6 space-y-4 text-xs">
              <div>
                <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Tenant Name</label>
                <input
                  type="text"
                  value={newTenantName}
                  onChange={(e) => setNewTenantName(e.target.value)}
                  placeholder="e.g. EMEA Wealth Management AI"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-xs text-slate-900 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Tenant Slug</label>
                <input
                  type="text"
                  value={newTenantSlug}
                  onChange={(e) => setNewTenantSlug(e.target.value)}
                  placeholder="e.g. emea-wealth-prod"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-xs font-mono text-slate-900 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Environment</label>
                  <select
                    value={newTenantEnv}
                    onChange={(e) => setNewTenantEnv(e.target.value as any)}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-xs text-slate-900 dark:text-white"
                  >
                    <option value="production">Production</option>
                    <option value="staging">Staging</option>
                    <option value="sandbox">Sandbox</option>
                  </select>
                </div>
                <div>
                  <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Compliance Standard</label>
                  <select
                    value={newTenantFramework}
                    onChange={(e) => setNewTenantFramework(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-xs text-slate-900 dark:text-white"
                  >
                    <option value="SOC 2 Type II">SOC 2 Type II</option>
                    <option value="NIST AI RMF 1.0">NIST AI RMF 1.0</option>
                    <option value="HIPAA Security">HIPAA Security</option>
                    <option value="ISO 42001">ISO 42001</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowCreateTenantModal(false)}
                  className="px-3 py-2 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-brand-600 hover:bg-brand-500 text-white shadow-sm"
                >
                  Create Tenant
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Admin Member & Role Management Suite */}
      <section className="glass-panel rounded-2xl p-6 sm:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Tenant Members & Access Control (RBAC)</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">Manage organization team members and their governance roles</p>
            </div>
          </div>

          {canAdminister && (
            <button
              type="button"
              onClick={() => setShowInviteModal(true)}
              className="inline-flex items-center gap-1.5 rounded-xl bg-brand-600 px-3.5 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-brand-500"
            >
              <UserPlus className="h-4 w-4" />
              <span>Invite Team Member</span>
            </button>
          )}
        </div>

        {memberError && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/20 dark:text-rose-300 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600 dark:text-rose-400" />
            <span>{memberError}</span>
          </div>
        )}

        {/* Members Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-slate-500 dark:text-slate-400 uppercase font-bold text-[10px] border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="py-3 px-4">Member</th>
                <th className="py-3 px-4">Role</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Joined Date</th>
                {canAdminister && <th className="py-3 px-4 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 font-medium text-slate-700 dark:text-slate-300">
              {membersLoading ? (
                <tr><td colSpan={canAdminister ? 5 : 4} className="py-8 px-4 text-center text-slate-500">Loading workspace members…</td></tr>
              ) : teamMembers.length === 0 ? (
                <tr><td colSpan={canAdminister ? 5 : 4} className="py-8 px-4 text-center text-slate-500">No members are available for this workspace.</td></tr>
              ) : teamMembers.map((member) => (
                <tr key={member.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/40">
                  <td className="py-3.5 px-4">
                    <div className="font-bold text-slate-900 dark:text-white text-sm">{member.name}</div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 font-mono">{member.email}</div>
                  </td>
                  <td className="py-3.5 px-4">
                    {canAdminister ? (
                      <select
                        value={member.role}
                        onChange={(e) => handleRoleChange(member.id, e.target.value as any)}
                        className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1 text-xs font-semibold text-slate-800 dark:text-slate-200 shadow-sm"
                      >
                        <option value="owner">👔 Workspace Owner</option>
                        <option value="admin">🛡️ AI Administrator</option>
                        <option value="operator">💻 Developer / Operator</option>
                        <option value="viewer">👁️ Read-Only Viewer</option>
                      </select>
                    ) : (
                      <span className="font-semibold capitalize text-slate-800 dark:text-slate-200">
                        {roleCopy[member.role].label}
                      </span>
                    )}
                  </td>
                  <td className="py-3.5 px-4">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                      member.status === 'active' 
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300' 
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300'
                    }`}>
                      {member.status === 'demo' ? 'demo only' : member.status}
                    </span>
                  </td>
                  <td className="py-3.5 px-4 text-slate-500 font-mono text-[11px]">
                    {member.joined_at || member.invited_at || '—'}
                  </td>
                  {canAdminister && (
                    <td className="py-3.5 px-4 text-right">
                      {member.id !== user?.id && member.status !== 'demo' ? (
                        <button
                          type="button"
                          onClick={() => handleRemoveMember(member)}
                          className="text-rose-600 hover:text-rose-700 dark:text-rose-400 text-xs font-semibold hover:underline"
                        >
                          {member.status === 'invited' ? 'Revoke' : 'Remove'}
                        </button>
                      ) : (
                        <span className="text-slate-400 text-[11px]">Current User</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Feature Permission Matrix (Collapsible) */}
        <details className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 p-4">
          <summary className="cursor-pointer list-none flex items-center justify-between text-xs font-bold text-slate-900 dark:text-white">
            <span className="flex items-center gap-2">
              <Sliders className="h-4 w-4 text-brand-600 dark:text-brand-400" />
              <span>Compare Role Permissions Matrix</span>
            </span>
            <span className="text-brand-600 dark:text-brand-400 text-[11px]">Expand comparison</span>
          </summary>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-[11px]">
              <thead className="border-b border-slate-200 dark:border-slate-800 uppercase font-bold text-slate-400 text-[10px]">
                <tr>
                  <th className="py-2 px-3">Platform Capability</th>
                  <th className="py-2 px-3 text-center">Owner</th>
                  <th className="py-2 px-3 text-center">AI Admin</th>
                  <th className="py-2 px-3 text-center">Developer/Operator</th>
                  <th className="py-2 px-3 text-center">Viewer/Auditor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-medium">
                <tr>
                  <td className="py-2 px-3 font-semibold text-slate-900 dark:text-white">Create / Delete Custom Guardrails</td>
                  <td className="py-2 px-3 text-center text-emerald-600">✓</td>
                  <td className="py-2 px-3 text-center text-emerald-600">✓</td>
                  <td className="py-2 px-3 text-center text-slate-400">—</td>
                  <td className="py-2 px-3 text-center text-slate-400">—</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-semibold text-slate-900 dark:text-white">Configure R2 Cold Archival & Legal Holds</td>
                  <td className="py-2 px-3 text-center text-emerald-600">✓</td>
                  <td className="py-2 px-3 text-center text-emerald-600">✓</td>
                  <td className="py-2 px-3 text-center text-slate-400">—</td>
                  <td className="py-2 px-3 text-center text-slate-400">—</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-semibold text-slate-900 dark:text-white">Quarantine Compromised Agents</td>
                  <td className="py-2 px-3 text-center text-emerald-600">✓</td>
                  <td className="py-2 px-3 text-center text-emerald-600">✓</td>
                  <td className="py-2 px-3 text-center text-slate-400">—</td>
                  <td className="py-2 px-3 text-center text-slate-400">—</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-semibold text-slate-900 dark:text-white">Register Applications & Rotate Scoped Keys</td>
                  <td className="py-2 px-3 text-center text-emerald-600">✓</td>
                  <td className="py-2 px-3 text-center text-emerald-600">✓</td>
                  <td className="py-2 px-3 text-center text-emerald-600">✓</td>
                  <td className="py-2 px-3 text-center text-slate-400">—</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-semibold text-slate-900 dark:text-white">Author Agent Profiles & Run Validation</td>
                  <td className="py-2 px-3 text-center text-emerald-600">✓</td>
                  <td className="py-2 px-3 text-center text-emerald-600">✓</td>
                  <td className="py-2 px-3 text-center text-emerald-600">✓</td>
                  <td className="py-2 px-3 text-center text-slate-400">—</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-semibold text-slate-900 dark:text-white">Export NIST AI RMF / SOC 2-Mapped Evidence Pack</td>
                  <td className="py-2 px-3 text-center text-emerald-600">✓</td>
                  <td className="py-2 px-3 text-center text-emerald-600">✓</td>
                  <td className="py-2 px-3 text-center text-emerald-600">✓</td>
                  <td className="py-2 px-3 text-center text-emerald-600">✓</td>
                </tr>
              </tbody>
            </table>
          </div>
        </details>
      </section>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="glass-panel w-full max-w-md rounded-2xl p-6 shadow-2xl border border-slate-200 dark:border-slate-800 animate-scale-in">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Invite Team Member</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Assign an RBAC role to grant workspace permissions.</p>

            <form onSubmit={handleInviteMember} className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Full Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Jordan Miller"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:border-brand-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Email Address</label>
                <input
                  type="email"
                  required
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="jordan@company.com"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:border-brand-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Assigned Role</label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value as any)}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:border-brand-500"
                >
                  <option value="operator">💻 AI Developer / Operator</option>
                  <option value="admin">🛡️ AI Administrator</option>
                  <option value="owner">👔 Workspace Owner</option>
                  <option value="viewer">👁️ Read-Only Auditor</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="px-3 py-2 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-brand-600 hover:bg-brand-500 text-white shadow-sm"
                >
                  Send Invite
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Role Navigation Cards */}
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="glass-panel rounded-2xl p-6">
          <div className="flex items-center gap-2">
            <Network className="h-4 w-4 text-brand-600 dark:text-brand-400" />
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Application Owner / Developer View</h2>
          </div>
          <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
            Connect an AI-built application, send its actions through the gateway, and inspect its traceable evidence.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setActiveTab('integrations')}
              disabled={!canManageApplications}
              className="inline-flex items-center gap-1.5 rounded-xl bg-brand-600 px-3 py-2 text-xs font-bold text-white hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <KeyRound className="h-3.5 w-3.5" />
              <span>Manage Integrations</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('observability')}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <Waypoints className="h-3.5 w-3.5" />
              <span>View Traces & DAG</span>
            </button>
          </div>
        </section>

        <section className="glass-panel rounded-2xl p-6">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">AI Administrator View</h2>
          </div>
          <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
            Oversee the organization fleet, intervention patterns, policies, retention, and response workflows from the central workspace.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setActiveTab('dashboard')}
              className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-500"
            >
              <ArrowRight className="h-3.5 w-3.5" />
              <span>Open Dashboard</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('policies')}
              disabled={!canAdminister}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <span>Review Policies</span>
            </button>
          </div>
        </section>
      </div>

      {/* Operator Audit Trail */}
      <details className="glass-panel rounded-2xl p-6">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Operator Audit Trail</h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Profile changes, release decisions, incident actions, and integration changes are recorded here.</p>
          </div>
          <span className="shrink-0 text-xs font-bold text-brand-700 dark:text-brand-300">{auditLoading ? 'Loading…' : `${auditLog.length} recent`}</span>
        </summary>
        {auditLoading ? (
          <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">Loading recent activity…</p>
        ) : auditLog.length === 0 ? (
          <p className="mt-4 rounded-xl border border-dashed border-slate-300 p-5 text-center text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">No operator actions have been recorded in this workspace yet.</p>
        ) : (
          <div className="mt-4 space-y-2">
            {auditLog.map((entry) => {
              const detail = entry.detail_json || {};
              const note = typeof detail.note === 'string' ? detail.note : typeof detail.change_reason === 'string' ? detail.change_reason : '';
              return (
                <div key={entry.id} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/50">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-bold text-slate-900 dark:text-white">{auditLabel(entry)}</span>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">{new Date(entry.created_at).toLocaleString()}</span>
                  </div>
                  <div className="mt-1 text-[11px] text-slate-600 dark:text-slate-400">
                    {entry.entity_type}{entry.entity_id ? ` · ${entry.entity_id}` : ''} · {entry.actor_email || 'Workspace operator'}
                  </div>
                  {note && (
                    <div className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
                      Note: {note}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </details>

    </div>
  );
};
